"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Label } from "@/components/ui/label";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { FootballScoreEditor } from "./score-editors/football-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type FootballTemplateData = {
  matchScore: { yourGoals: number; opponentGoals: number };
  periods?: Array<{ label: string; yourScore: number; opponentScore: number }>;
  penalties?: { yourScore: number; opponentScore: number } | null;
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type FootballParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type FootballTemplateProps = {
  currentUser: FootballParticipant;
  onChange: (
    data: FootballTemplateData,
    participants: FootballParticipant[],
  ) => void;
  initialData?: Partial<FootballTemplateData>;
};

export function FootballTemplate({
  currentUser,
  onChange,
  initialData,
}: FootballTemplateProps) {
  const [participants, setParticipants] = useState<FootballParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));
  type FootballScorePart = Pick<
    FootballTemplateData,
    "matchScore" | "periods" | "penalties"
  >;
  const [scorePart, setScorePart] = useState<FootballScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourGoals: 0, opponentGoals: 0 },
    periods: initialData?.periods ?? [],
    penalties: initialData?.penalties ?? null,
  }));

  // Flexible team sizes for football (allows subs and casual games)
  const maxPerTeam = 25;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  useEffect(() => {
    const data: FootballTemplateData = {
      matchScore: scorePart.matchScore,
      periods: scorePart.periods ?? [],
      penalties: scorePart.penalties ?? null,
      positions: teams,
      hasScore: true, // Always true for activity posts creation
    };
    onChange(data, participants);
  }, [scorePart, teams, participants, onChange]);

  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    if (teamCount >= maxPerTeam) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourGoals = scorePart.matchScore.yourGoals;
  const opponentGoals = scorePart.matchScore.opponentGoals;
  const penaltiesWinner = (() => {
    const penalties = scorePart.penalties;
    if (!penalties) return null;
    if (penalties.yourScore === penalties.opponentScore) return null;
    return penalties.yourScore > penalties.opponentScore ? "team1" : "team2";
  })();

  const yourWon =
    yourGoals > opponentGoals ||
    (yourGoals === opponentGoals && penaltiesWinner === "team1");
  const opponentWon =
    opponentGoals > yourGoals ||
    (yourGoals === opponentGoals && penaltiesWinner === "team2");
  const isDraw =
    yourGoals === opponentGoals && penaltiesWinner == null && yourGoals > 0;
  const hasOpponent = opponentTeam.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <TeamCard
          label="Team 1"
          isYourTeam
          players={yourTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon || isDraw}
        />
        <TeamCard
          label="Team 2"
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={maxPerTeam}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
        />
      </div>

      {!hasOpponent && (
        <div className="rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-warning">
          <span>Add at least one player to Team 2</span>
        </div>
      )}

      <FootballScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as FootballScorePart)}
      />

      {/* Preview in creation flow */}
      {hasOpponent && (
        <div className="space-y-2">
          <Label className="text-text-primary">Pitch Preview</Label>
          <ActivityCourtPreview
            activityId="football"
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

type FootballDisplayProps = {
  data: FootballTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function FootballDisplay({ data, participants }: FootballDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="football"
      data={data}
      participants={participants}
    />
  );
}
